import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customerService } from '../../services/customerService';
import { workOrderService } from '../../services/workOrderService';
import { FiArrowLeft, FiUser, FiPhone, FiMail, FiMapPin, FiPackage, FiCalendar } from 'react-icons/fi';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const customerData = await customerService.getById(id);
      if (customerData) {
        setCustomer(customerData);
        // Ambil work orders customer ini
        const allWO = await workOrderService.getAll();
        const customerWO = allWO.filter(wo => wo.customerId === id);
        setWorkOrders(customerWO);
      }
    } catch (error) {
      console.error('Error loading customer detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-600">❌ Customer tidak ditemukan</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/customers')}
          className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
        >
          <FiArrowLeft size={24} className="text-[var(--text-primary)]" />
        </button>
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Customer Detail</h1>
      </div>

      {/* Customer Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">{customer.name}</h2>
            <div className="space-y-3">
              <p className="flex items-center space-x-2 text-[var(--text-secondary)]">
                <FiUser className="text-blue-500" />
                <span>{customer.name}</span>
              </p>
              <p className="flex items-center space-x-2 text-[var(--text-secondary)]">
                <FiPhone className="text-green-500" />
                <span>{customer.phone}</span>
              </p>
              {customer.email && (
                <p className="flex items-center space-x-2 text-[var(--text-secondary)]">
                  <FiMail className="text-purple-500" />
                  <span>{customer.email}</span>
                </p>
              )}
              {customer.address && (
                <p className="flex items-center space-x-2 text-[var(--text-secondary)]">
                  <FiMapPin className="text-red-500" />
                  <span>{customer.address}</span>
                </p>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-[var(--border-color)] grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[var(--text-secondary)]">Total Orders</p>
                <p className="text-2xl font-bold text-[var(--text-primary)]">{customer.totalOrders || 0}</p>
              </div>
              {customer.lastService && (
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Last Service</p>
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {new Date(customer.lastService).toLocaleDateString('id-ID')}
                  </p>
                </div>
              )}
            </div>

            {customer.notes && (
              <div className="mt-4 p-3 bg-[var(--bg-secondary)] rounded-lg">
                <p className="text-sm font-medium text-[var(--text-secondary)]">Notes</p>
                <p className="text-sm text-[var(--text-primary)]">{customer.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Work Orders */}
        <div className="lg:col-span-2">
          <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center space-x-2">
              <FiPackage size={20} />
              <span>Work Orders ({workOrders.length})</span>
            </h3>

            {workOrders.length === 0 ? (
              <p className="text-[var(--text-secondary)] text-center py-8">Belum ada work order untuk customer ini</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--bg-secondary)]">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">WO Number</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Project</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Status</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-[var(--text-secondary)]">Target</th>
                      <th className="px-4 py-2 text-center text-sm font-medium text-[var(--text-secondary)]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workOrders.map((wo) => (
                      <tr key={wo.id} className="border-t border-[var(--border-color)]">
                        <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{wo.woNumber}</td>
                        <td className="px-4 py-2 text-sm text-[var(--text-primary)]">{wo.projectName}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(wo.status)}`}>
                            {wo.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-[var(--text-secondary)]">
                          {wo.targetDate || '-'}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => navigate(`/work-orders/${wo.id}`)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;